import { Component, ChangeDetectionStrategy, signal, inject, computed } from '@angular/core';
import { Router, NavigationEnd, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { SidebarComponent } from '../../components/sidebar/sidebar.component';
import { HeaderComponent } from '../../components/header/header.component';
import { BottomNavComponent } from '../../components/bottom-nav/bottom-nav.component';
import { AuthService } from '../../services/auth.service';
import { UserRole } from '../../models/user.model';

@Component({
  selector: 'app-main-layout',
  imports: [RouterOutlet, SidebarComponent, HeaderComponent, BottomNavComponent],
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MainLayoutComponent {
  sidebarOpen = signal(false);
  isSidebarCollapsed = signal(false);
  private currentUrl = signal('');

  private authService = inject(AuthService);

  /** Hidden on dashboard and for operator / approver / admin (mobile only via template). */
  showBottomNav = computed(() => {
    const url = this.currentUrl();
    const role = this.authService.currentUser()?.role;
    const hideForRole =
      role === UserRole.OPERATOR ||
      role === UserRole.APPROVER ||
      role === UserRole.ADMIN;
    const hideOnRoleRoute =
      url.includes('/dhamnagar-dashboard/operator') ||
      url.includes('/dhamnagar-dashboard/approver') ||
      url.includes('/dhamnagar-dashboard/user-management');
    const hideOnDashboard = url.startsWith('/dhamnagar-dashboard/dashboard');
    const hideOnProjectDetail = url.includes('/dhamnagar-dashboard/project/');
    return !hideOnDashboard && !hideForRole && !hideOnRoleRoute && !hideOnProjectDetail;
  });

  constructor(private router: Router) {
    this.currentUrl.set(this.router.url);

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        const url = this.router.url;
        this.currentUrl.set(url);

        // Auto-collapse sidebar on detail-heavy pages (all roles)
        const shouldCollapse =
          url.includes('departments') ||
          url.includes('priorities') ||
          url.includes('hm-priorities') ||
          url.includes('field-execution') ||
          url.includes('scheme') ||
          url.includes('department-master') ||
          url.includes('project');

        // Auto-collapse on intervention routes for OSD/Minister to maximise table visibility
        const shouldCollapseForRole =
          this.authService.isMinisterOrOsd() && url.includes('intervention');

        this.isSidebarCollapsed.set(shouldCollapse || shouldCollapseForRole);
      });
  }

  toggleSidebar(): void {
    this.sidebarOpen.update(v => !v);
  }

  closeSidebar(): void {
    this.sidebarOpen.set(false);
  }
}
